import { supabase } from '../lib/supabase';
import { Assistant, AssistantConversation, AssistantMessage } from '../types/assistant';

export class AssistantService {
  // CRUD para Assistentes
  async createAssistant(assistant: Omit<Assistant, 'id' | 'createdAt' | 'updatedAt' | 'usageCount'>): Promise<Assistant> {
    const { data, error } = await supabase
      .from('assistants')
      .insert({
        name: assistant.name,
        description: assistant.description,
        instructions: assistant.instructions,
        avatar: assistant.avatar,
        ai_provider: assistant.aiProvider,
        model_id: assistant.modelId,
        temperature: 0.7, // Valor padrão
        max_tokens: 2000, // Valor padrão
        is_public: assistant.isPublic,
        created_by: assistant.createdBy,
        tenant_id: assistant.tenantId,
        tags: assistant.tags,
        category: assistant.category,
        tools: assistant.tools || [],
        usage_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) {
      console.error('Erro ao criar assistente:', error);
      throw new Error('Falha ao criar assistente');
    }

    return this.mapAssistantFromDB(data);
  }

  async getAssistantsByTenant(tenantId: string): Promise<Assistant[]> {
    const { data, error } = await supabase
      .from('assistants')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar assistentes:', error);
      throw new Error('Falha ao buscar assistentes');
    }

    return data.map(this.mapAssistantFromDB);
  }

  async getAssistantsByUser(userId: string, tenantId: string): Promise<Assistant[]> {
    // Buscar assistentes que o usuário pode ver:
    // 1. Criados pelo próprio usuário (created_by = userId)
    // 2. Públicos do tenant (is_public = true)
    // 3. Com visibility = 'team' (toda empresa)
    // 4. Com visibility = 'shared' E userId em shared_with_users
    
    console.log('🔍 Buscando assistentes para:', { userId, tenantId });
    
    const { data, error } = await supabase
      .from('assistants')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('❌ Erro ao buscar assistentes do usuário:', error);
      throw new Error('Falha ao buscar assistentes');
    }

    // Filtrar pelo lado do cliente considerando todas as regras de visibilidade
    const filteredData = (data || []).filter(assistant => {
      // 1. Criado pelo próprio usuário
      if (assistant.created_by === userId) {
        return true;
      }
      
      // 2. Público (is_public = true)
      if (assistant.is_public === true) {
        return true;
      }
      
      // 3. Visibilidade "team" (toda empresa)
      if (assistant.visibility === 'team') {
        return true;
      }
      
      // 4. Visibilidade "shared" e usuário está na lista
      if (assistant.visibility === 'shared' && assistant.shared_with_users) {
        return assistant.shared_with_users.includes(userId);
      }
      
      return false;
    });

    console.log('✅ Assistentes encontrados:', filteredData.length);
    console.log('📋 Detalhes:', filteredData.map(a => ({ 
      id: a.id, 
      name: a.name, 
      created_by: a.created_by, 
      is_public: a.is_public,
      visibility: a.visibility,
      tenant_id: a.tenant_id 
    })));

    return filteredData.map(this.mapAssistantFromDB);
  }

  async updateAssistant(id: string, updates: Partial<Assistant>): Promise<Assistant> {
    const updateData: any = {
      updated_at: new Date().toISOString()
    };

    // Mapear campos camelCase para snake_case
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.instructions !== undefined) updateData.instructions = updates.instructions;
    if (updates.avatar !== undefined) updateData.avatar = updates.avatar;
    if (updates.aiProvider !== undefined) updateData.ai_provider = updates.aiProvider;
    if (updates.modelId !== undefined) updateData.model_id = updates.modelId;
    if (updates.isPublic !== undefined) updateData.is_public = updates.isPublic;
    if (updates.tags !== undefined) updateData.tags = updates.tags;
    if (updates.category !== undefined) updateData.category = updates.category;
    if (updates.tools !== undefined) updateData.tools = updates.tools;

    const { data, error } = await supabase
      .from('assistants')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Erro ao atualizar assistente:', error);
      throw new Error('Falha ao atualizar assistente');
    }

    return this.mapAssistantFromDB(data);
  }

  async deleteAssistant(id: string): Promise<void> {
    const { error } = await supabase
      .from('assistants')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Erro ao deletar assistente:', error);
      throw new Error('Falha ao deletar assistente');
    }
  }

  async incrementUsageCount(assistantId: string): Promise<void> {
    const { error } = await supabase.rpc('increment_assistant_usage', {
      assistant_id: assistantId
    });

    if (error) {
      console.error('Erro ao incrementar uso do assistente:', error);
    }
  }

  // CRUD para Conversas de Assistentes
  async createAssistantConversation(
    assistantId: string, 
    userId: string, 
    tenantId: string,
    title?: string
  ): Promise<AssistantConversation> {
    const { data, error } = await supabase
      .from('assistant_conversations')
      .insert({
        assistant_id: assistantId,
        user_id: userId,
        tenant_id: tenantId,
        title: title || 'Nova conversa',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) {
      console.error('Erro ao criar conversa do assistente:', error);
      throw new Error('Falha ao criar conversa');
    }

    return {
      id: data.id,
      assistantId: data.assistant_id,
      title: data.title,
      messages: [],
      userId: data.user_id,
      tenantId: data.tenant_id,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at)
    };
  }

  async getAssistantConversations(assistantId: string, userId: string): Promise<AssistantConversation[]> {
    const { data, error } = await supabase
      .from('assistant_conversations')
      .select('*')
      .eq('assistant_id', assistantId)
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar conversas do assistente:', error);
      throw new Error('Falha ao buscar conversas');
    }

    return data.map((conv: any) => ({
      id: conv.id,
      assistantId: conv.assistant_id,
      title: conv.title,
      messages: [],
      userId: conv.user_id,
      tenantId: conv.tenant_id,
      createdAt: new Date(conv.created_at),
      updatedAt: new Date(conv.updated_at)
    }));
  }

  async getAssistantConversationWithMessages(conversationId: string): Promise<AssistantConversation | null> {
    // Buscar conversa
    const { data: convData, error: convError } = await supabase
      .from('assistant_conversations')
      .select('*')
      .eq('id', conversationId)
      .single();

    if (convError || !convData) {
      console.error('Erro ao buscar conversa:', convError);
      return null;
    }

    // Buscar mensagens
    const { data: messagesData, error: messagesError } = await supabase
      .from('assistant_messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('timestamp', { ascending: true });

    if (messagesError) {
      console.error('Erro ao buscar mensagens:', messagesError);
      throw new Error('Falha ao buscar mensagens');
    }

    const messages: AssistantMessage[] = messagesData.map((msg: any) => ({
      id: msg.id,
      content: msg.content,
      role: msg.role,
      timestamp: new Date(msg.timestamp),
      assistantId: msg.assistant_id,
      conversationId: msg.conversation_id,
      attachments: msg.attachments || undefined,
      messageType: msg.message_type || 'message',
      toolId: msg.tool_id || undefined,
      toolName: msg.tool_name || undefined,
      toolOperation: msg.tool_operation || undefined,
      toolParameters: msg.tool_parameters || undefined,
      toolResult: msg.tool_result || undefined,
      toolStatus: msg.tool_status || undefined
    }));

    return {
      id: convData.id,
      assistantId: convData.assistant_id,
      title: convData.title,
      messages,
      userId: convData.user_id,
      tenantId: convData.tenant_id,
      createdAt: new Date(convData.created_at),
      updatedAt: new Date(convData.updated_at)
    };
  }

  async addMessageToAssistantConversation(
    conversationId: string,
    assistantId: string,
    content: string,
    role: 'user' | 'assistant' | 'system',
    tokens?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number },
    modelId?: string,
    aiProvider?: string,
    attachments?: Array<{ name: string; size: number; type: string; url?: string }>,
    options?: {
      messageType?: 'message' | 'tool' | 'final_answer';
      toolId?: string;
      toolName?: string;
      toolOperation?: string;
      toolParameters?: Record<string, any>;
      toolResult?: any;
      toolStatus?: 'running' | 'completed' | 'failed';
    }
  ): Promise<AssistantMessage> {
    // Calcular custo se tokens estiverem disponíveis (silenciosamente)
    let cost = 0;
    if (tokens?.prompt_tokens && tokens?.completion_tokens && modelId) {
      const { calculateMessageCost } = await import('./cost-calculator');
      cost = calculateMessageCost(
        modelId,
        tokens.prompt_tokens,
        tokens.completion_tokens,
        aiProvider
      );
    }

    const { data, error } = await supabase
      .from('assistant_messages')
      .insert({
        conversation_id: conversationId,
        assistant_id: assistantId,
        content,
        role,
        timestamp: new Date().toISOString(),
        prompt_tokens: tokens?.prompt_tokens || 0,
        completion_tokens: tokens?.completion_tokens || 0,
        total_tokens: tokens?.total_tokens || 0,
        cost: cost,
        attachments: attachments || null,
        message_type: options?.messageType || 'message',
        tool_id: options?.toolId || null,
        tool_name: options?.toolName || null,
        tool_operation: options?.toolOperation || null,
        tool_parameters: options?.toolParameters || null,
        tool_result: options?.toolResult || null,
        tool_status: options?.toolStatus || null
      })
      .select()
      .single();

    if (error) {
      console.error('Erro ao adicionar mensagem:', error);
      throw new Error('Falha ao adicionar mensagem');
    }

    // Atualizar timestamp da conversa
    await supabase
      .from('assistant_conversations')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', conversationId);

    return {
      id: data.id,
      content: data.content,
      role: data.role,
      timestamp: new Date(data.timestamp),
      assistantId: data.assistant_id,
      conversationId: data.conversation_id,
      attachments: data.attachments || undefined,
      messageType: data.message_type || 'message',
      toolId: data.tool_id || undefined,
      toolName: data.tool_name || undefined,
      toolOperation: data.tool_operation || undefined,
      toolParameters: data.tool_parameters || undefined,
      toolResult: data.tool_result || undefined,
      toolStatus: data.tool_status || undefined
    };
  }

  async updateConversationTitle(conversationId: string, title: string): Promise<void> {
    const { error } = await supabase
      .from('assistant_conversations')
      .update({ 
        title,
        updated_at: new Date().toISOString()
      })
      .eq('id', conversationId);

    if (error) {
      console.error('Erro ao atualizar título da conversa:', error);
      throw new Error('Falha ao atualizar título');
    }
  }

  async deleteAssistantConversation(conversationId: string): Promise<void> {
    // Deletar mensagens primeiro
    await supabase
      .from('assistant_messages')
      .delete()
      .eq('conversation_id', conversationId);

    // Deletar conversa
    const { error } = await supabase
      .from('assistant_conversations')
      .delete()
      .eq('id', conversationId);

    if (error) {
      console.error('Erro ao deletar conversa:', error);
      throw new Error('Falha ao deletar conversa');
    }
  }

  // Utilitários
  private mapAssistantFromDB(data: any): Assistant {
    return {
      id: data.id,
      name: data.name,
      description: data.description,
      instructions: data.instructions,
      avatar: data.avatar,
      aiProvider: data.ai_provider,
      modelId: data.model_id,
      isPublic: data.is_public,
      createdBy: data.created_by,
      tenantId: data.tenant_id,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
      tags: data.tags || [],
      category: data.category,
      usageCount: data.usage_count || 0,
      visibility: data.visibility || 'private',
      sharedWithUsers: data.shared_with_users || [],
      tools: data.tools || []
    };
  }

  // Buscar assistentes populares/recomendados
  async getPopularAssistants(tenantId: string, limit: number = 10): Promise<Assistant[]> {
    const { data, error } = await supabase
      .from('assistants')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('is_public', true)
      .order('usage_count', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Erro ao buscar assistentes populares:', error);
      return [];
    }

    return data.map(this.mapAssistantFromDB);
  }

  // Buscar assistentes por categoria
  async getAssistantsByCategory(tenantId: string, category: string): Promise<Assistant[]> {
    const { data, error} = await supabase
      .from('assistants')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('category', category)
      .order('usage_count', { ascending: false });

    if (error) {
      console.error('Erro ao buscar assistentes por categoria:', error);
      return [];
    }

    return data.map(this.mapAssistantFromDB);
  }

  // Atualizar configurações de compartilhamento
  async updateAssistantSharing(
    assistantId: string,
    visibility: 'private' | 'team' | 'shared',
    sharedWithUsers: string[]
  ): Promise<void> {
    const { error } = await supabase
      .from('assistants')
      .update({
        visibility,
        shared_with_users: sharedWithUsers
      })
      .eq('id', assistantId);

    if (error) {
      console.error('Erro ao atualizar compartilhamento:', error);
      throw error;
    }
  }
}

export const assistantService = new AssistantService();
